import * as PRTNR from "./prtnr";

import styled from "@emotion/styled";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InfoCircleIcon } from "./assets";
import GenericModal from "./GenericModal";
import ModalUpdateSuccess from "./ModalUpdateSuccess";
import {
  createOrUpdateStripeAccount,
  fetchStripeAccount,
} from "./redux/slices/MembersThunks";
import { useAddressNow } from "./useAddressNow";
import { validateNationalInsuranceNumber } from "./utils/validateNationalInsuranceNumber";

function PaymentDetails({ discardOnclick }) {
  const dispatch = useDispatch();

  const member = JSON.parse(localStorage.getItem("member"));

  // Error states
  const [stripeError, setstripeError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const { account, loading, error } = useSelector((state) => state.members);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    accountHolderName: "",
    accountNumber: "",
    confirmAccountNumber: "",
    ifscSwiftCode: "",
    dateOfBirth: "",
    streetAddress: "",
    postalCode: "",
    nationalInsuranceNumber: "",
  });
  const [initialFormData, setInitialFormData] = useState({});

  const streetAddressRef = useRef();
  const postalCodeRef = useRef();

  const openSuccessModal = () => setIsSuccessModalOpen(true);

  const validateBankDetails = (data) => {
    const errors = {};

    // Required field validation
    if (!data.accountHolderName?.trim()) {
      errors.accountHolderName = "Account Holder Name is required";
    }

    if (!data.accountNumber?.trim()) {
      errors.accountNumber = "Account Number is required";
    } else if (data.accountNumber.includes("****")) {
      errors.accountNumber = "Please enter a valid account number";
    }

    if (!data.confirmAccountNumber?.trim()) {
      errors.confirmAccountNumber = "Please confirm your account number";
    } else if (data.confirmAccountNumber.includes("****")) {
      errors.confirmAccountNumber = "Please enter a valid account number";
    }

    if (
      data.accountNumber &&
      data.confirmAccountNumber &&
      !data.accountNumber.includes("****") &&
      !data.confirmAccountNumber.includes("****")
    ) {
      if (data.accountNumber !== data.confirmAccountNumber) {
        errors.confirmAccountNumber = "Account numbers do not match";
      }
    }

    if (!data.ifscSwiftCode?.trim()) {
      errors.ifscSwiftCode = "Sort Code is required";
    } else if (!/^[A-Z0-9]{4,11}$/i.test(data.ifscSwiftCode)) {
      errors.ifscSwiftCode =
        "Enter a valid Sort code (4-11 alphanumeric characters)";
    }

    if (!data.dateOfBirth?.trim()) {
      errors.dateOfBirth = "Date of Birth is required";
    } else {
      const dob = new Date(data.dateOfBirth);
      const today = new Date();

      if (isNaN(dob.getTime())) {
        errors.dateOfBirth = "Invalid date of birth";
      } else {
        const age = today.getFullYear() - dob.getFullYear();
        const ageCheck =
          age > 18 ||
          (age === 18 &&
            (today.getMonth() > dob.getMonth() ||
              (today.getMonth() === dob.getMonth() &&
                today.getDate() >= dob.getDate())));

        if (!ageCheck) {
          errors.dateOfBirth = "You must be at least 18 years old";
        }
      }
    }

    if (!data.streetAddress?.trim()) {
      errors.streetAddress = "Street Address is required";
    }

    if (!data.postalCode?.trim()) {
      errors.postalCode = "Postal Code is required";
    }
    if (data.nationalInsuranceNumber?.trim()) {
      const niValidationError = validateNationalInsuranceNumber(
        data.nationalInsuranceNumber
      );
      if (niValidationError && niValidationError.trim()) {
        errors.nationalInsuranceNumber = niValidationError;
      }
    }

    return errors;
  };

  const handleInputChange = (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value,
    };

    setFormData(newFormData);

    if (hasSubmitted) {
      const validationErrors = validateBankDetails(newFormData);
      setFieldErrors((prev) => ({
        ...prev,
        [field]: validationErrors[field] || "",
      }));
    } else {
      if (fieldErrors[field]) {
        setFieldErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    setHasSubmitted(true);

    const streetValue =
      streetAddressRef.current?.value || formData.streetAddress;
    const postalValue = postalCodeRef.current?.value || formData.postalCode;

    const updatedFormData = {
      ...formData,
      streetAddress: streetValue,
      postalCode: postalValue,
    };

    setFormData(updatedFormData);
    try {
      const validationErrors = validateBankDetails(updatedFormData);
      setFieldErrors(validationErrors);

      if (Object.keys(validationErrors).length > 0) {
        setIsLoading(false);
        return;
      }

      const [year, month, day] = updatedFormData.dateOfBirth.split("-");

      if (!year || !month || !day) {
        return;
      }

      const payload = {
        account_holder_name: updatedFormData.accountHolderName,
        account_number: updatedFormData.accountNumber,
        confirm_account_number: updatedFormData.confirmAccountNumber,
        sort_code: updatedFormData.ifscSwiftCode || updatedFormData.sortCode,
        date_of_birth: {
          day: parseInt(day, 10),
          month: parseInt(month, 10),
          year: parseInt(year, 10),
        },
        home_address: updatedFormData.streetAddress,
        postal_code: updatedFormData.postalCode,
        nino: updatedFormData.nationalInsuranceNumber || undefined,
      };

      await dispatch(createOrUpdateStripeAccount(payload)).unwrap();
      await dispatch(fetchStripeAccount()).unwrap();

      openSuccessModal();
      setFieldErrors({});
      setHasSubmitted(false);
    } catch (error) {
      console.error("Bank details save error:", error);
      const errorMessage =
        error ||
        error?.payload?.message ||
        "Failed to update bank details. Please try again.";
      setstripeError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    dispatch(fetchStripeAccount()).catch((error) => {
      console.error("Failed to fetch Stripe account:", error);
      setstripeError("Failed to load bank details. Please refresh the page.");
    });
  }, [dispatch]);

  useEffect(() => {
    if (account) {
      try {
        let acc = account?.stripe_account?.live;

        if (!acc) {
          console.warn("No live Stripe account found");
          return;
        }

        const externalAccount = acc?.external_accounts?.data?.[0] || {};
        const dob = acc?.individual?.dob || { day: "", month: "", year: "" };

        const formatDob = ({ day, month, year }) => {
          if (!day || !month || !year) return "";
          try {
            const d = String(day).padStart(2, "0");
            const m = String(month).padStart(2, "0");
            return `${year}-${m}-${d}`;
          } catch (error) {
            console.error("Date formatting error:", error);
            return "";
          }
        };

        const newFormData = {
          accountHolderName: externalAccount.account_holder_name || "",
          bankName: externalAccount.bank_name || "",
          accountNumber: externalAccount.last4
            ? `**** **** **** ${externalAccount.last4}`
            : "",
          confirmAccountNumber: externalAccount.last4
            ? `**** **** **** ${externalAccount.last4}`
            : "",
          ifscSwiftCode: externalAccount?.routing_number || "",
          branchName: acc.company?.phone || "",
          country: acc.country || "",
          postalCode: acc?.company?.address?.postal_code || "",
          streetAddress: acc?.company?.address?.line1 || "",
          dateOfBirth: formatDob(dob),
          nationalInsuranceNumber: member?.nino || "",
        };

        setFormData(newFormData);
        setInitialFormData(newFormData);
      } catch (error) {
        console.error("Error processing account data:", error);
        setstripeError("Error loading account data");
      }
    }
  }, [account, member?.nino]);

  const API_KEY = process.env.REACT_APP_ADDRESSNOW_KEY;

  useAddressNow(
    API_KEY,
    (addressnow) => {
      addressnow.setConfig({
        fields: {
          line1: "#paymentStreet2",
          postcode: "#paymentPostcode2",
        },
        trigger: "#paymentStreet2",
      });
    },
    []
  );

  return (
    <Frame1501>
      <Frame1496>
        <Frame1502>
          <PRTNR.Input
            label="Account Holder Name"
            value={formData.accountHolderName}
            onChange={(e) =>
              handleInputChange("accountHolderName", e.target.value)
            }
            required
            error={!!fieldErrors.accountHolderName}
            errorMessage={fieldErrors.accountHolderName || "Required Field"}
          />
        </Frame1502>

        <Frame1502>
          <PRTNR.Input
            label="Account Number"
            value={formData.accountNumber}
            onChange={(e) => handleInputChange("accountNumber", e.target.value)}
            required
            error={!!fieldErrors.accountNumber}
            errorMessage={fieldErrors.accountNumber || "Required Field"}
          />
        </Frame1502>

        <Frame1502>
          <PRTNR.Input
            label="Confirm Account Number"
            value={formData.confirmAccountNumber}
            onChange={(e) =>
              handleInputChange("confirmAccountNumber", e.target.value)
            }
            required
            error={!!fieldErrors.confirmAccountNumber}
            errorMessage={fieldErrors.confirmAccountNumber || "Required Field"}
          />
        </Frame1502>

        <Frame1502>
          <PRTNR.Input
            label="Sort Code"
            value={formData.ifscSwiftCode}
            onChange={(e) => handleInputChange("ifscSwiftCode", e.target.value)}
            required
            error={!!fieldErrors.ifscSwiftCode}
            errorMessage={fieldErrors.ifscSwiftCode || "Required Field"}
          />

          <PRTNR.Input
            label={
              <LabelWrapper>
                <span>Date of Birth</span> <Required>*</Required>
                <TooltipWrapper>
                  <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                  <TooltipText className="tooltip">
                    You must be a UK resident and at least 18 years of age or
                    older to become a Member on the Paartner Referral Platform.
                    You must provide your legal full name, a valid e-mail
                    address, and any other information requested in order to
                    complete the sign-up process to become a Member (a “Paartner
                    Account”). By signing up, you confirm that you are legally
                    capable of entering into binding agreements and accept full
                    responsibility for your use of the Platform, including any
                    tax or legal obligations that may arise.
                  </TooltipText>
                </TooltipWrapper>
              </LabelWrapper>
            }
            type="date"
            value={formData.dateOfBirth}
            onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
            error={!!fieldErrors.dateOfBirth}
            errorMessage={fieldErrors.dateOfBirth || "Required Field"}
          />
        </Frame1502>

        <Frame1502>
          <PRTNR.Input
            ref={streetAddressRef}
            id="paymentStreet2"
            name="paymentStreet2"
            label="Street address"
            defaultValue={formData.streetAddress}
            onChange={(e) => handleInputChange("streetAddress", e.target.value)}
            required
            error={!!fieldErrors.streetAddress}
            errorMessage={fieldErrors.streetAddress || "Required Field"}
          />
          <PRTNR.Input
            ref={postalCodeRef}
            id="paymentPostcode2"
            name="paymentPostcode2"
            label="Postal code"
            defaultValue={formData.postalCode}
            onChange={(e) => handleInputChange("postalCode", e.target.value)}
            required
            error={!!fieldErrors.postalCode}
            errorMessage={fieldErrors.postalCode || "Required Field"}
          />
        </Frame1502>

        <PRTNR.Input
          label={
            <LabelWrapper>
              <span>National Insurance Number</span>
              <TooltipWrapper>
                <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                <TooltipText className="tooltip">
                  You've earned rewards above the HMRC reporting threshold. For
                  compliance, we now need your National Insurance number to
                  share securely with HMRC.
                </TooltipText>
              </TooltipWrapper>
            </LabelWrapper>
          }
          value={formData.nationalInsuranceNumber}
          onChange={(e) =>
            handleInputChange("nationalInsuranceNumber", e.target.value)
          }
          error={!!fieldErrors.nationalInsuranceNumber}
          errorMessage={fieldErrors.nationalInsuranceNumber}
        />
        {stripeError && <ErrorMessage>{stripeError}</ErrorMessage>}
      </Frame1496>

      <Frame1500>
        <PRTNR.Button
          variant="gray-light"
          onClick={() => {
            // If account existed => reset to initial data
            if (initialFormData && Object.keys(initialFormData).length > 0) {
              setFormData(initialFormData);
            } else {
              // No account existed => empty form
              setFormData({
                accountHolderName: "",
                accountNumber: "",
                confirmAccountNumber: "",
                ifscSwiftCode: "",
                dateOfBirth: "",
                streetAddress: "",
                postalCode: "",
                nationalInsuranceNumber: "",
              });
            }

            // Clear validation + errors
            setFieldErrors({});
            setHasSubmitted(false);
            setstripeError("");

            // Optional parent callback
            if (discardOnclick) discardOnclick();
          }}
        >
          Discard
        </PRTNR.Button>

        <Frame1399>
          <PRTNR.Button
            variant="black"
            disabled={isLoading}
            onClick={handleSave}
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </PRTNR.Button>
        </Frame1399>
      </Frame1500>

      <GenericModal
        isOpen={isSuccessModalOpen}
        onClose={() => {
          setIsSuccessModalOpen(false);
        }}
      >
        <ModalUpdateSuccess
          isOpen={isSuccessModalOpen}
          onClose={() => {
            setIsSuccessModalOpen(false);
          }}
        />
      </GenericModal>
    </Frame1501>
  );
}
export default PaymentDetails;

const Frame1502 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
  @media (max-width: 1024px) and (min-width: 768px) {
    flex-direction: column;
  }

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;
const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex; /* keeps inline behavior but allows flexbox */
  align-items: center; /* vertical centering */
  justify-content: center; /* horizontal centering */

  &:hover .tooltip {
    visibility: visible;
    opacity: 1;
  }
`;

const GFeildsInfo = styled.img`
  width: 14px;
  height: 14px;
  object-fit: cover;
`;

const LabelWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  box-sizing: border-box;
  flex-shrink: 0;
  span {
    margin: 0;
  }
`;
const Required = styled.div`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-weight: 700;
  margin-left: 4px;
`;

const TooltipText = styled.div`
  visibility: hidden;
  color: #000000;
  font-weight: 500;
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  text-align: center;
  padding: 8px 16px;
  border-radius: 4px;
  position: absolute;
  z-index: 1;
  top: 125%; /* Show below the icon */
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s;
  max-width: 280px;
  width: 280px;
  backdrop-filter: blur(20px);
  background: var(--backgrounds-primary, #ffffff);

  &::after {
    content: "";
    position: absolute;
    bottom: 100%; /* Top of the tooltip box */
    left: 50%;
    margin-left: -6px;
    border-width: 6px;
    border-style: solid;
    border-color: transparent transparent var(--backgrounds-primary, #ffffff)
      transparent;
  }
`;

const Frame1501 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-end;
  flex: 1;
  gap: 21px;
  width: 886px;
  box-sizing: border-box;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const Frame1496 = styled.form`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  border: solid 1px rgb(225, 228, 233);
  border-radius: 16px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 16px;
  flex-shrink: 0;
`;

const Frame1500 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  flex: none;
  gap: 17px;
  box-sizing: border-box;
`;

const Frame1399 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  flex: none;
  gap: 16px;
  box-sizing: border-box;
`;
const ErrorMessage = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-family: Montserrat, sans-serif;
  margin-top: 4px;
  text-align: left;
  width: 100%;
`;
